<template>
	<div
		class="band-wrapper"
		:class="{ 'band-wrapper-warning': bandWarningMessages.length > 0 }"
	>
		<LabeledSwitch
			v-model="bandRef.checked"
			:small="true"
			:rightLabel="'band ' + bandRef.key"
			:allowWordWrap="false"
			:disabled="subNetworkDeletedRef"
		/>
		<Select
			class="flex-grow"
			mode="multiple"
			v-model:value="bandRef.selectedSignals"
			:options="selectOptions"
			:getPopupContainer="getPopupContainer"
			:disabled="subNetworkDeletedRef"
		/>
		<ul v-if="bandWarningMessages.length" class="band-warning">
			<li v-for="message in bandWarningMessages" :key="message">
				{{ message }}
			</li>
		</ul>
	</div>
</template>

<script>
import { toRef, computed } from 'vue';
import { useVModel } from '@vueuse/core';

import { Select } from 'ant-design-vue';

import { useI18n } from 'vue-i18n'

import Consts from '@/api/Consts';

import { usePopupContainer } from '@/utils/usePopupContainer';

import LabeledSwitch from '@/components/common/LabeledSwitch';


const NOT_RECOMMENDED_SIGNALS = {
	'C': ['C7I', 'C7Q', 'C7X'],
}


const getNotRecommendedSignals = (gnssCode) => {
	return NOT_RECOMMENDED_SIGNALS[gnssCode] || [];
}


export default {
	components: {
		Select, LabeledSwitch,
	},
	props: ['subNetworkDeleted', 'gnssCode', 'band', 'vrsSignalsStats'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const gnssCodeRef = toRef(props, 'gnssCode');
		const bandRef = useVModel(props, 'band', emit);
		const vrsSignalsStatsRef = toRef(props, 'vrsSignalsStats');
		const subNetworkDeletedRef = toRef(props, 'subNetworkDeleted');

		const filterBandStatsSignals = (gnssCode, bandKey) => {
			const gnssStats = vrsSignalsStatsRef.value.find(item => item.gnssCode === gnssCode);
			if (!gnssStats) {
				return [];
			}
			const bandStats = gnssStats.bands.find(band => band.key === bandKey);
			if (!bandStats) {
				return [];
			}
			return bandStats.signals;
		};

		const bandWarningMessages = computed(() => {
			if (subNetworkDeletedRef.value || vrsSignalsStatsRef.value.length === 0) {
				return [];
			}
			const messages = [];
			const statsSignalsData = filterBandStatsSignals(gnssCodeRef.value, bandRef.value.key);
			const statsSignals = statsSignalsData.map(item => item.signal);

			const notRecommendedSignals = getNotRecommendedSignals(gnssCodeRef.value);
			const statsSignalsDataWithoutBlacklist = filterBandStatsSignals(gnssCodeRef.value, bandRef.value.key).filter(item => !notRecommendedSignals.includes(item.signal));
			const statsSignalsWithoutBlacklist = statsSignals.filter(signal => !notRecommendedSignals.includes(signal));

			const selectedSignals = (bandRef.value.selectedSignals || []).filter(signal => signal.startsWith('C'));
			const bandLabel = bandRef.value.key;
			const gnssLabel = t('common.gnssLabels.' + gnssCodeRef.value);

			const missingInMeasurements = selectedSignals.filter(signal => !statsSignals.includes(signal));
			if (missingInMeasurements.length > 0) {
				messages.push(t('settings.vrsSettingsForm.activeSignalsMessages.missingInMeasurements', {
					gnssCode: gnssLabel,
					band: bandLabel,
					signals: missingInMeasurements.join(', '),
				}));
			}

			const extraMeasurements = statsSignalsWithoutBlacklist.filter(signal => !selectedSignals.includes(signal));
			if (extraMeasurements.length > 0) {
				messages.push(t('settings.vrsSettingsForm.activeSignalsMessages.notInSettings', {
					gnssCode: gnssLabel,
					band: bandLabel,
					signals: extraMeasurements.join(', '),
				}));
			}

			const selectedFirst = selectedSignals[0];
			if (selectedFirst && statsSignalsDataWithoutBlacklist.length > 0) {
				const maxCount = Math.max(...statsSignalsDataWithoutBlacklist.map(item => item.signals_count));
				const measuredFirst = statsSignalsDataWithoutBlacklist
					.filter(item => item.signals_count === maxCount)
					.map(item => item.signal);
				if (!measuredFirst.includes(selectedFirst)) {
					messages.push(t('settings.vrsSettingsForm.activeSignalsMessages.firstSignalMismatch', {
						gnssCode: gnssLabel,
						band: bandLabel,
						measured: measuredFirst.join(', '),
						selected: selectedFirst,
					}));
				}
			}

			const notRecommendedSelected = selectedSignals.filter(signal => notRecommendedSignals.includes(signal));
			if (notRecommendedSelected.length > 0) {
				messages.push(t('settings.vrsSettingsForm.activeSignalsMessages.notRecommendedSignals', {
					gnssCode: gnssLabel,
					band: bandLabel,
					signals: notRecommendedSelected.join(', '),
				}));
			}

			return messages;
		});

		const selectOptions = computed(() => {
			const statsSignalsData = filterBandStatsSignals(gnssCodeRef.value, bandRef.value.key);
			const statsCounts = {};
			for (const item of statsSignalsData) {
				statsCounts[item.signal] = item.signals_count;
			}
			return (bandRef.value.signals || []).map(option => {
				const count = statsCounts[option.value];
				const label = (count != null && count > 0) ? option.value + ' (' + count + ')' : option.value;
				return {
					value: option.value,
					label,
				};
			});
		});

		const { getPopupContainer } = usePopupContainer();
		return {
			gnssCodeRef,
			subNetworkDeletedRef,
			bandRef,
			bandWarningMessages,
			selectOptions,
			getPopupContainer,
			WARNING_LIGHT: Consts.COLORS.warningLight,
			WARNING_DARK: Consts.COLORS.warningDark,
		};
	},
}
</script>

<style scoped>
.band-wrapper {
	width: 100%;
	display: flex;
	align-items: center;
	gap: 8px;
	border: 1px solid transparent;
	border-radius: 6px;
	padding: 8px;
	flex-wrap: wrap;
}

.band-wrapper-warning {
	border-color: v-bind(WARNING_DARK);
	background-color: v-bind(WARNING_LIGHT);
}

ul.band-warning {
	padding-inline-start: 12px;
	margin-block: 0;
	font-size: 12px;
	line-height: 16px;
	color: v-bind(WARNING_DARK);
}

.band-warning li {
	margin-block-end: 6px;
}
</style>